import { headerCommonPanelSX } from '@/components/content/Header/styles/headerCommonPanel';
import { Box, ClickAwayListener, Drawer, Fade, Popper, useMediaQuery } from '@mui/material';
import { SxProps, Theme, useTheme } from '@mui/material/styles';
import { FC, ReactElement, useCallback, useEffect, useRef, useState } from 'react';

type Props = {
	idPopper: string;
	isOpen: boolean;
	handleClose: () => void;
	anchorEl: HTMLElement | null;
	children: ReactElement;
	needDrawer?: boolean;
	closeAutomatically?: boolean;
	sx?: SxProps<Theme>;
};

export const HeaderCommonPanel: FC<Props> = ({
	idPopper,
	isOpen,
	handleClose,
	children,
	anchorEl,
	needDrawer = true,
	closeAutomatically = false,
	sx,
}) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
	const drawerStatus = needDrawer && isMobile && isOpen;

	const [hasInteracted, setHasInteracted] = useState(!closeAutomatically);
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);

	const handlePopperClose = useCallback(() => {
		handleClose();
		setHasInteracted(!closeAutomatically);
	}, [handleClose, closeAutomatically]);

	useEffect(() => {
		if (!isMobile && isOpen && !hasInteracted) {
			timeoutRef.current = setTimeout(() => {
				handlePopperClose();
			}, 5000);
		}

		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
				timeoutRef.current = null;
			}
		};
	}, [isOpen, isMobile, hasInteracted, handlePopperClose]);

	const handleInteraction = () => {
		if (!hasInteracted) {
			setHasInteracted(true);
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
				timeoutRef.current = null;
			}
		}
	};

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				handleClose();
				const target = event.target as HTMLElement;
				target?.blur();
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [handleClose]);

	useEffect(() => {
		handleClose();
	}, [isMobile]);

	return (
		<>
			<Popper
				id={idPopper}
				open={!isMobile && isOpen}
				anchorEl={anchorEl}
				transition
				sx={[headerCommonPanelSX.popperContainer(theme), ...(Array.isArray(sx) ? sx : [sx])]}
				placement="bottom-end"
			>
				{({ TransitionProps }) => (
					<Fade {...TransitionProps} timeout={500} easing={{ enter: 'ease-in', exit: 'ease-out' }}>
						<Box>
							<ClickAwayListener onClickAway={handlePopperClose}>
								<Box onMouseEnter={handleInteraction} onClick={handleInteraction}>
									{children}
								</Box>
							</ClickAwayListener>
						</Box>
					</Fade>
				)}
			</Popper>
			{needDrawer ? (
				<Fade
					in={drawerStatus}
					timeout={500}
					style={{
						transitionTimingFunction: drawerStatus ? 'ease-in' : 'ease-out',
					}}
				>
					<Box>
						<Drawer
							open={drawerStatus}
							anchor={'bottom'}
							onClose={handleClose}
							PaperProps={{
								id: idPopper,
							}}
						>
							<Box sx={headerCommonPanelSX.drawerContainer(theme)}>{children}</Box>
						</Drawer>
					</Box>
				</Fade>
			) : null}
		</>
	);
};
